import { FC, useEffect } from 'react';

import { useEntitySchema } from '@april9au/stack9-react';
import * as ui from '@april9au/stack9-ui';
import dayjs from 'dayjs';
import { z } from 'zod';

import { HealthScreen as S9HealthScreen } from '../../models/stack9/HealthScreen';

const S9EntityActions = ui.S9EntityActions.default;
const S9Drawer = ui.S9Drawer.default;
const S9Form = ui.S9Form.default;
const S9Fieldset = ui.S9Fieldset.default;
const S9Button = ui.S9Button.default;
const S9PageSection = ui.S9PageSection.default;

const { useForm } = ui.S9Form;

interface Props {
  open: boolean;
  id: number | undefined;
  canEdit?: boolean;
  onSaved(): void;
  onDeleted(): void;
  onClose(): void;
}

type HealthScreen = z.infer<typeof S9HealthScreen>;

const HealthScreenDrawer: FC<Props> = ({
  open,
  id = 0,
  canEdit = true,
  onSaved,
  onDeleted,
  onClose,
}) => {
  const [form] = useForm();

  const { schema, isValidating } = useEntitySchema('health_screen');

  const { handleSave, isLoading: isSaving } =
    ui.useEntityActions('health_screen');

  const { data: record } = ui.useScreenQueryById<HealthScreen>(
    'getHealthScreenById',
    id,
    { shouldFetch: !!id },
  );

  useEffect(() => {
    form.resetFields();
  }, [open, form, id, record]);

  return (
    <S9Drawer
      open={open}
      closable
      keyboard={false}
      maskClosable={false}
      onClose={onClose}
      width={880}
    >
      <S9Form
        form={form}
        disabled={isSaving || isValidating || canEdit === false}
        initialValues={record}
        loading={isValidating}
        onFinish={async values => {
          await handleSave(
            S9HealthScreen.parse({
              ...values,
              screening_dt: values.screening_dt
                ? dayjs(values.screening_dt).toISOString()
                : null,
              follow_up_dt: values.follow_up_dt
                ? dayjs(values.follow_up_dt).toISOString()
                : null,
            } satisfies S9HealthScreen),
            id,
          );

          onSaved();
          onClose();
        }}
      >
        <S9Drawer.Header
          title={id ? `Update health screen #${id}` : 'Add new health screen'}
          extra={
            <>
              <S9Button label="Cancel" onClick={onClose} />
              <S9EntityActions
                form={form}
                entityKey="health_screen"
                entityId={undefined}
                disabled={isSaving}
                isSaving={isSaving}
                onDeleted={onDeleted}
                filterPrintableDoc={() => false}
              />
            </>
          }
        />
        <S9Drawer.Content>
          {schema && (
            <>
              <S9PageSection>
                <S9Fieldset
                  context={{ id }}
                  schema={schema}
                  formFields={[
                    {
                      field: 'form_type',
                      colSize: 12,
                    },
                    {
                      field: 'screening_dt',
                      colSize: 12,
                    },
                    {
                      field: 'client_id',
                      colSize: 12,
                    },
                    {
                      field: 'practitioner',
                      colSize: 12,
                    },
                  ]}
                />
              </S9PageSection>

              <S9PageSection title="Scores">
                <S9Fieldset
                  context={{ id }}
                  schema={schema}
                  formFields={[
                    {
                      field: 'depression_score',
                      colSize: 8,
                    },
                    {
                      field: 'anxiety_score',
                      colSize: 8,
                    },
                    {
                      field: 'stress_score',
                      colSize: 8,
                    },
                    {
                      field: 'overall_rating',
                      colSize: 8,
                    },
                    {
                      field: 'follow_up_required',
                      colSize: 8,
                    },
                    {
                      field: 'follow_up_dt',
                      colSize: 8,
                    },
                  ]}
                />
              </S9PageSection>
            </>
          )}
        </S9Drawer.Content>
      </S9Form>
    </S9Drawer>
  );
};

export default HealthScreenDrawer;
